import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axiosInstance, { checkTokenValidity, getUserDetails, getStaffId } from "../../components/content/axiosConfig";
import { fetchPondsForCurrentUser } from "../../utils/pondListForUser";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import LengthUnitDropDownSelection from "../../components/custom/LengthUnitDropDownSelection";
import WeightUnitDropDownSelection from "../../components/custom/WeightUnitDropDown";
import FishTypesSelection from "../../components/custom/FishTypesSelection";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";
import SamplingEffortFields from "../../components/custom/SamplingEffortFields";
import ClockTimeInput from "../../components/custom/ClockTimeInput";
import {
  requiresEffortUnits,
  isAnglingSamplingMethod,
  SAMPLING_METHOD_OPTIONS,
  isValidClockTimeInput,
  toClockTimeValue,
  normalizeSamplingMethod,
} from "../../utils/samplingCpueUtils";
import { displayMethodLabel } from "../../utils/samplingDisplayLabels";

/**
 * Optional UpdatePD UI blocks — hidden by default (same idea as create pond). State, handlers, and submit payload are unchanged.
 * Set any flag to `true` to show that block again. Entire panels are omitted when off (no empty cards).
 */
const SHOW_UPDATE_PD_WEATHER_PANEL = false;
const SHOW_UPDATE_PD_WATER_QUALITY_PANEL = true;
const SHOW_UPDATE_PD_FERTILIZATION_DATE_TYPE = false;

const showWeatherPanel = SHOW_UPDATE_PD_WEATHER_PANEL;
const showWaterQualityPanel = SHOW_UPDATE_PD_WATER_QUALITY_PANEL;
const showFertilizationDateType = SHOW_UPDATE_PD_FERTILIZATION_DATE_TYPE;

const WQ_FIELD_STYLE = {
  border: '1px solid #e0e0e0',
  borderRadius: '10px',
  height: '50px',
  fontSize: '0.95rem',
  transition: 'all 0.3s ease',
  backgroundColor: '#fafafa',
};

const wqFocusGreen = (e) => {
  e.target.style.borderColor = '#32a932';
  e.target.style.backgroundColor = '#fff';
  e.target.style.boxShadow = '0 0 0 3px rgba(50, 169, 50, 0.1)';
};
const wqBlurDefault = (e) => {
  e.target.style.borderColor = '#e0e0e0';
  e.target.style.backgroundColor = '#fafafa';
  e.target.style.boxShadow = 'none';
};

const WQ_EXTRA_FIELD_DEFS = [
  { id: 'BOD5', label: 'BOD5, mg/L' },
  { id: 'COD', label: 'COD, mg/L' },
  { id: 'Alkalinity', label: 'Alkalinity, mg/L CaCO3' },
  { id: 'NitriteN', label: 'Nitrite-N, mg/L NO2-N' },
  { id: 'TotalHardness', label: 'Total Hardness, mg/L CaCO3' },
  { id: 'PhosphateIon', label: 'Phosphate Ion, mg/L PO4' },
  { id: 'Phosphorous', label: 'Phosphorus, mg/L P' },
  { id: 'TotalAmmoniaNitrogen', label: 'Total Ammonia Nitrogen, mg/L NH3-N' },
];

const optionalWQFloat = (v) => {
  if (v === '' || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const wqNumToInputString = (v) => (v != null && v !== '' ? String(v) : '');

/** Full width on lg when weather and water panels are both hidden (layout matches create pond Physical card). */
const fertilizationUnitsColClass =
  showWeatherPanel || showWaterQualityPanel ? "col-lg-6" : "col-lg-12";

const UpdatePD = () => {
  const history = useHistory();
  const { id } = useParams(); // PondDetail ID
  const sessionFisherID = getUserDetails()?.FisherID;
  const [detailFisherID, setDetailFisherID] = useState(null);
  const fisherID = detailFisherID ?? sessionFisherID;
  const [ponds, setPonds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [pondFishTypes, setPondFishTypes] = useState([]);
  const [samplingFishTypes, setSamplingFishTypes] = useState([]);
  const [initialSamplingFishTypes, setInitialSamplingFishTypes] = useState([]); // Store initial fish types
  const [selectedWeightUnit, setSelectedWeightUnit] = useState("");
  const [selectedLengthUnit, setSelectedLengthUnit] = useState("");

  // Helper functions for date formatting (US Month-Day-Year for session file names)
  const formatDate = (dateObj) => {
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj)) return "";
    const day = dateObj.getDate().toString().padStart(2, "0");
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const year = dateObj.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const formatLocalDateTime = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Initial form state
  const initialFormState = {
    PondDetailID: id,
    PondID: "0",
    Name: "",
    Date: null,
    TimeFishingStarted: "",
    TimeFishingStopped: "",
    SamplingMethod: "",
    AirTemperature: "",
    SkyClimate: "",
    WindSpeed: "",
    WindDirection: "",
    MoonPhase: "",
    BaroMetricPressure: "",
    WaterTemperature: "",
    WaterClarity: "",
    PH: "",
    DO: "",
    BOD5: "",
    COD: "",
    Alkalinity: "",
    NitriteN: "",
    TotalHardness: "",
    PhosphateIon: "",
    Phosphorous: "",
    TotalAmmoniaNitrogen: "",
    FertilizationDate: null,
    FertilizationType: "",
    NoFishes: 0,
    NoFishTypes: 0,
    FisherID: "",
    Weight_Unit: "",
    Length_Unit: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [effortCount, setEffortCount] = useState("");
  const [effortUnits, setEffortUnits] = useState([]);
  const [anglerCount, setAnglerCount] = useState("");
  const [fisherStaffOptions, setFisherStaffOptions] = useState([]);
  const [selectedSessionAnglers, setSelectedSessionAnglers] = useState([]);
  const [fishSamples, setFishSamples] = useState([]);
  const [pondAnglers, setPondAnglers] = useState([]);

  const fetchFishSamples = useCallback(async () => {
    if (!id) return;
    try {
      const res = await axiosInstance.get("/api/FishSampling/SamplingData", {
        params: { PondDID: id },
      });
      setFishSamples(Array.isArray(res.data) ? res.data : []);
    } catch {
      setFishSamples([]);
    }
  }, [id]);

  const fetchSessionAnglers = useCallback(async (pondDetailId) => {
    if (!pondDetailId) {
      setPondAnglers([]);
      setSelectedSessionAnglers([]);
      return;
    }
    try {
      const res = await axiosInstance.get("/api/PondDetail/PondAnglers", {
        params: { PondDID: pondDetailId },
      });
      const anglers = res.data?.anglers || [];
      setPondAnglers(anglers);
      const selected = anglers.map((a) => ({
        value: a.staffId,
        label: a.fullName,
      }));
      setSelectedSessionAnglers(selected);
      if (selected.length > 0) {
        setAnglerCount(String(selected.length));
      }
    } catch {
      setPondAnglers([]);
      setSelectedSessionAnglers([]);
    }
  }, []);

  const fetchFisherTeamStaff = useCallback(async (scopedFisherID) => {
    const fid = scopedFisherID ?? fisherID ?? formData.FisherID;
    if (!fid) {
      setFisherStaffOptions([]);
      return;
    }
    try {
      const res = await axiosInstance.get("/api/PondDetail/FisherTeamStaff", {
        params: { FisherID: fid },
      });
      const options = (res.data?.staff || []).map((s) => ({
        value: s.id,
        label: s.fullName,
      }));
      setFisherStaffOptions(options);
    } catch {
      setFisherStaffOptions([]);
    }
  }, [fisherID, formData.FisherID]);

  const handleSessionAnglersChange = (selected) => {
    const next = selected || [];
    setSelectedSessionAnglers(next);
    setAnglerCount(next.length > 0 ? String(next.length) : "");
    setPondAnglers(
      next.map((s) => ({ staffId: s.value, fullName: s.label }))
    );
    setFormErrors((prev) => ({ ...prev, SessionAnglers: "" }));
  };

  const saveSessionAnglers = async (pondDetailId, selected) => {
    if (!selected?.length) return;
    await axiosInstance.put(`/api/SamplingSessionAnglers/${pondDetailId}`, {
      staffIds: selected.map((s) => s.value),
    });
  };

  const saveEffortUnits = async (pondDetailId, samplingMethod, units) => {
    if (!requiresEffortUnits(samplingMethod) || !units?.length) return;
    await axiosInstance.put(`/api/SamplingEffort/${pondDetailId}`, {
      samplingMethod,
      units,
    });
  };

  // Fetch fish types for sampling data (selected fish types for PondDetail)
  const FetchFishTypesOfSamplingData = useCallback(async (opts = {}) => {
    const skipLoading = opts.skipLoading === true;
    try {
      if (!skipLoading) setIsLoading(true);
      const response = await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${id}`);
      const fishTypes = response.data || [];

      // Map to react-select format
      const formattedFishTypes = fishTypes.map((fish) => ({
        value: fish.FishTypeID,
        label: fish.FishTypeName,
        pondFishesID: fish.PondFishesID,
      }));

      setSamplingFishTypes(formattedFishTypes);
      setInitialSamplingFishTypes(formattedFishTypes); // Save initial fish types
      setFormData((prev) => ({
        ...prev,
        NoFishTypes: formattedFishTypes.length,
      }));
    } catch (error) {
      handleApiError(error, "Failed to fetch sampling fish types");
    } finally {
      if (!skipLoading) setIsLoading(false);
    }
  }, [id]);

  // Fetch fish types for the selected pond (all available options)
  const fetchFishTypesOfPonds = useCallback(async (pondId, opts = {}) => {
    if (!pondId || pondId === "0") return;
    const skipLoading = opts.skipLoading === true;

    try {
      if (!skipLoading) setIsLoading(true);
      const response = await axiosInstance.get(`/api/PondFishes/GetAllPondFishes/${pondId}`, {
        params: { search: "" },
      });

      setPondFishTypes(response.data || []);
    } catch (error) {
      handleApiError(error, "Failed to fetch pond fish types");
    } finally {
      if (!skipLoading) setIsLoading(false);
    }
  }, []);

  // Fetch ponds data
  const fetchPonds = useCallback(async (opts = {}) => {
    const skipLoading = opts.skipLoading === true;
    try {
      if (!skipLoading) setIsLoading(true);
      const fetchedPonds = await fetchPondsForCurrentUser(getUserDetails());
      setPonds(fetchedPonds);
    } catch (error) {
      handleApiError(error, "Failed to fetch ponds");
    } finally {
      if (!skipLoading) setIsLoading(false);
    }
  }, []);

  // Fetch pond detail data
  const FetchPondDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      const detailParams = { id };
      if (sessionFisherID != null && sessionFisherID !== "") {
        detailParams.FisherID = sessionFisherID;
      }
      const response = await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
        params: detailParams,
      });
      const data = response.data;
      const resolvedFisherID = data.FisherID ?? sessionFisherID;
      if (resolvedFisherID != null) {
        setDetailFisherID(resolvedFisherID);
      }
      console.log("pond Detail---", data);
      const formattedData = {
        ...data,
        PondDetailID: id,
        PondID: data.PondID?.toString() || "0",
        FisherID: resolvedFisherID ?? "",
        SamplingMethod: normalizeSamplingMethod(data.SamplingMethod) || data.SamplingMethod || "",
        Date: data.Date ? new Date(data.Date) : null,
        FertilizationDate: data.FertilizationDate ? new Date(data.FertilizationDate) : null,
        TimeFishingStarted: data.TimeFishingStarted
          ? toClockTimeValue(data.TimeFishingStarted)
          : "",
        TimeFishingStopped: data.TimeFishingStopped
          ? toClockTimeValue(data.TimeFishingStopped)
          : "",
        AirTemperature: data.AirTemperature?.toString() || "",
        WindSpeed: data.WindSpeed?.toString() || "",
        BaroMetricPressure: data.BaroMetricPressure?.toString() || "",
        WaterTemperature: data.WaterTemperature?.toString() || "",
        WaterClarity: wqNumToInputString(data.WaterClarity),
        PH: data.PH?.toString() || "",
        DO: data.DO != null && data.DO !== '' ? String(data.DO) : '',
        BOD5: wqNumToInputString(data.BOD5),
        COD: wqNumToInputString(data.COD),
        Alkalinity: wqNumToInputString(data.Alkalinity),
        NitriteN: wqNumToInputString(data.NitriteN),
        TotalHardness: wqNumToInputString(data.TotalHardness),
        PhosphateIon: wqNumToInputString(data.PhosphateIon),
        Phosphorous: wqNumToInputString(data.Phosphorous),
        TotalAmmoniaNitrogen: wqNumToInputString(data.TotalAmmoniaNitrogen),
        NoFishes: data.NoFishes || 0,
        NoFishTypes: data.NoFishTypes || 0,
        Weight_Unit: data.Weight_Unit?.toString() || "",
        Length_Unit: data.Length_Unit?.toString() || "",
      };

      // Normalize session file name to US MM-DD-YYYY (older saves used DD-MM-YYYY).
      if (formattedData.Date instanceof Date && !isNaN(formattedData.Date)) {
        const datePart = formatDate(formattedData.Date);
        const storedName = String(data.Name || "");
        const pondPrefix =
          data.PondName ||
          (storedName.replace(/-\d{1,2}-\d{1,2}-\d{4}$/, "") !== storedName
            ? storedName.replace(/-\d{1,2}-\d{1,2}-\d{4}$/, "")
            : "");
        formattedData.Name = pondPrefix
          ? `${pondPrefix}-${datePart}`
          : datePart;
      }

      const units = data.EffortUnits || [];
      setEffortUnits(units);
      setEffortCount(units.length ? String(units.length) : "");
      const sessionAnglers = data.SessionAnglers || [];
      const selected = sessionAnglers.map((a) => ({
        value: a.staffId,
        label: a.fullName,
      }));
      setSelectedSessionAnglers(selected);
      setPondAnglers(sessionAnglers);
      setAnglerCount(
        selected.length > 0
          ? String(selected.length)
          : data.NumberOfAnglers != null && Number(data.NumberOfAnglers) > 0
            ? Number(data.NumberOfAnglers)
            : ""
      );

      setFormData(formattedData);
      setSelectedWeightUnit(formattedData.Weight_Unit);
      setSelectedLengthUnit(formattedData.Length_Unit);

      if (formattedData.PondID && formattedData.PondID !== "0") {
        await fetchFishTypesOfPonds(formattedData.PondID, { skipLoading: true });
      }
      await fetchFishSamples();
      if (isAnglingSamplingMethod(formattedData.SamplingMethod)) {
        await fetchFisherTeamStaff(resolvedFisherID);
        if (!sessionAnglers.length) {
          await fetchSessionAnglers(id);
        }
      } else {
        setPondAnglers([]);
        setSelectedSessionAnglers([]);
      }
      await fetchPonds({ skipLoading: true });

    } catch (error) {
      handleApiError(error, "Failed to fetch pond detail");
    } finally {
      setIsLoading(false);
    }
  }, [sessionFisherID, id, fetchFishTypesOfPonds, fetchPonds, fetchFishSamples, fetchFisherTeamStaff, fetchSessionAnglers]);

  // Handle API errors
  const handleApiError = (error, defaultMessage) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      window.location.reload();
      showErrorToast("Session expired. Please log in again.");
    } else {
      const errorMessage = error.response?.data?.message || defaultMessage;
      showErrorToast(errorMessage);
      console.error("API Error:", error);
    }
  };

  // Handle pond change
  const handlePondChange = (e) => {
    const { value } = e.target;
    const selectedPond = ponds.find((item) => item.id === Number(value));

    setFormErrors((prev) => ({ ...prev, PondID: "" }));
    setFormData((prev) => ({
      ...prev,
      PondID: value,
      Name: selectedPond ? `${selectedPond.Name}-${formatDate(prev.Date || new Date())}` : formatDate(prev.Date || new Date()),
    }));

    // Fetch fish types for the new pond
    fetchFishTypesOfPonds(value);
    // Reset selected fish types when pond changes
    setSamplingFishTypes([]);
    setFormData((prev) => ({ ...prev, NoFishTypes: 0 }));
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormErrors((prev) => ({ ...prev, [id]: "" }));
    if (id === "SamplingMethod") {
      setEffortCount("");
      setEffortUnits([]);
      if (!isAnglingSamplingMethod(value)) {
        setAnglerCount("");
        setSelectedSessionAnglers([]);
        setPondAnglers([]);
      }
    }
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // Handle date changes — ignore empty/invalid picker events (they used to snap to now).
  const handleDateChange = (e, type) => {
    const newDateTime = e.target.value;
    if (!newDateTime) return;
    const newDate = new Date(newDateTime);
    if (Number.isNaN(newDate.getTime())) return;

    if (type === 1) {
      const formattedDate = formatDate(newDate);
      const selectedPond = ponds.find((item) => item.id === Number(formData.PondID));
      setFormData((prev) => ({
        ...prev,
        Date: newDate,
        Name: selectedPond ? `${selectedPond.Name}-${formattedDate}` : formattedDate,
      }));
    } else if (type === 2) {
      setFormData((prev) => ({
        ...prev,
        FertilizationDate: newDate,
      }));
    }
  };

  // Handle unit selection changes
  const handleWeightUnitChange = (selectedOption) => {
    const weightUnitValue = selectedOption?.value || "";
    setSelectedWeightUnit(weightUnitValue);
    setFormData((prev) => ({ ...prev, Weight_Unit: weightUnitValue }));
    setFormErrors((prev) => ({ ...prev, Weight_Unit: "" }));
  };

  const handleLengthUnitChange = (selectedOption) => {
    const lengthUnitValue = selectedOption?.value || "";
    setSelectedLengthUnit(lengthUnitValue);
    setFormData((prev) => ({ ...prev, Length_Unit: lengthUnitValue }));
    setFormErrors((prev) => ({ ...prev, Length_Unit: "" }));
  };

  // Handle fish types change
  const handleFishTypesChange = (selectedOptions) => {
    setSamplingFishTypes(selectedOptions || []);
    setFormErrors((prev) => ({ ...prev, FishTypes: "" }));
    setFormData((prev) => ({
      ...prev,
      NoFishTypes: selectedOptions?.length || 0,
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    const requiredFields = [
      { field: "PondID", message: "Please select a pond", check: (val) => val && val !== "0" },
      { field: "Name", message: "Name is required", check: (val) => val?.trim() },
      { field: "TimeFishingStarted", message: "Time fishing started is required (h:mm AM/PM)", check: (val) => val?.trim() && isValidClockTimeInput(val) },
      { field: "TimeFishingStopped", message: "Time fishing stopped is required (h:mm AM/PM)", check: (val) => val?.trim() && isValidClockTimeInput(val) },
      { field: "SamplingMethod", message: "Sampling method is required", check: (val) => val?.trim() },
      { field: "Weight_Unit", message: "Weight unit is required", check: (val) => val },
      { field: "Length_Unit", message: "Length unit is required", check: (val) => val },
      { field: "FishTypes", message: "At least one fish type is required", check: () => samplingFishTypes.length > 0 },
    ];

    requiredFields.forEach(({ field, message, check }) => {
      if (!check(formData[field])) {
        errors[field] = message;
        isValid = false;
      }
    });

    if (isAnglingSamplingMethod(formData.SamplingMethod)) {
      if (!selectedSessionAnglers.length) {
        errors.SessionAnglers = "Select at least one angler for this fishing session";
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const performUpdate = async (exitAfterSave) => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Update PondDetail
      const payload = {
        ...formData,
        FisherID: fisherID ?? formData.FisherID,
        Date: formData.Date?.toISOString() || new Date().toISOString(),
        FertilizationDate: formData.FertilizationDate?.toISOString() || null,
        NumberOfAnglers: isAnglingSamplingMethod(formData.SamplingMethod)
          ? parseInt(anglerCount, 10)
          : null,
        BOD5: optionalWQFloat(formData.BOD5),
        COD: optionalWQFloat(formData.COD),
        Alkalinity: optionalWQFloat(formData.Alkalinity),
        NitriteN: optionalWQFloat(formData.NitriteN),
        TotalHardness: optionalWQFloat(formData.TotalHardness),
        PhosphateIon: optionalWQFloat(formData.PhosphateIon),
        Phosphorous: optionalWQFloat(formData.Phosphorous),
        TotalAmmoniaNitrogen: optionalWQFloat(formData.TotalAmmoniaNitrogen),
      };

      await axiosInstance.put(`/api/PondDetail/UpdatePondDetail/${id}`, payload);
      await saveEffortUnits(id, formData.SamplingMethod, effortUnits);
      if (isAnglingSamplingMethod(formData.SamplingMethod)) {
        await saveSessionAnglers(id, selectedSessionAnglers);
      }

      // Step 2: Update SamplingFishes
      const currentFishTypeIds = samplingFishTypes.map((fish) => fish.value);
      const initialFishTypeIds = initialSamplingFishTypes.map((fish) => fish.value);

      // Fish types to add (in current but not in initial)
      const fishTypesToAdd = currentFishTypeIds.filter((fid) => !initialFishTypeIds.includes(fid));

      // Fish types to delete (in initial but not in current)
      const fishTypesToDelete = initialSamplingFishTypes.filter(
        (fish) => !currentFishTypeIds.includes(fish.value)
      );

      // Add new fish types
      for (const fishTypeId of fishTypesToAdd) {
        await axiosInstance.post(`/api/SamplingFishes/AddFish`, {
          PondDID: id,
          FishTypeID: fishTypeId,
          FisherID: fisherID ?? formData.FisherID,
          NumberOfFishes: 0,
          AverageLength: 0,
          StandardDeviationLength: 0,
          AverageWeight: 0,
          StandardDeviationWeight: 0,
          AverageWr: 0,
          StandardDeviationWr: 0,
          NumberOfFishesReleased: 0,
          NumberOfFishesCulled: 0,
        });
      }

      // Delete removed fish types
      for (const fish of fishTypesToDelete) {
        if (fish.pondFishesID) {
          await axiosInstance.delete(`/api/SamplingFishes/DeleteFish/${fish.pondFishesID}`);
        }
      }

      showSuccessToast(
        exitAfterSave
          ? "Pond detail updated — returning to Fishing Data"
          : "Pond detail and fish types updated successfully"
      );

      if (exitAfterSave) {
        history.push("/fishingData");
      } else {
        await FetchFishTypesOfSamplingData({ skipLoading: true });
      }
    } catch (error) {
      handleApiError(error, "Failed to update pond detail or fish types");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const sub = e.nativeEvent.submitter;
    const action = sub?.getAttribute?.("value") ?? sub?.value ?? "stay";
    const exitAfterSave = action === "exit";
    await performUpdate(exitAfterSave);
  };

  // Initialize once per pond-detail id.
  useEffect(() => {
    let cancelled = false;
    checkTokenValidity();
    (async () => {
      await FetchPondDetail();
      if (cancelled) return;
      await FetchFishTypesOfSamplingData({ skipLoading: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only remount/reload when route id changes
  }, [id]);

  useEffect(() => {
    if (isAnglingSamplingMethod(formData.SamplingMethod)) {
      fetchFisherTeamStaff(fisherID ?? formData.FisherID);
    } else {
      setFisherStaffOptions([]);
      setSelectedSessionAnglers([]);
      setPondAnglers([]);
    }
  }, [formData.SamplingMethod, formData.FisherID, fisherID, fetchFisherTeamStaff]);

  useEffect(() => {
    const refreshFishSamples = () => {
      if (document.visibilityState === "visible") {
        fetchFishSamples();
      }
    };
    document.addEventListener("visibilitychange", refreshFishSamples);
    return () => document.removeEventListener("visibilitychange", refreshFishSamples);
  }, [fetchFishSamples]);

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page donezo-workflow-page py-3">
      <form onSubmit={handleFormSubmit}>
        <div className="container-fluid px-3 px-md-4">
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
            <div className="mb-3 mb-md-0 flex-grow-1" style={{ minWidth: 0 }}>
              <PondDetailWorkflowPageHeader
                pondDetailId={id}
                section="general"
                detailName={formData.Name}
                noBottomMargin
              />
            </div>
            <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
              <button
                type="submit"
                name="saveAction"
                value="stay"
                className="btn btn-outline-success px-3 px-md-4 py-2 rounded-pill shadow-sm"
                style={{
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                }}
                disabled={isSubmitting}
                onMouseOver={(e) => {
                  if (!isSubmitting) e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {isSubmitting ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                ) : (
                  <i className="fa fa-save me-2" aria-hidden />
                )}
                Update
              </button>
              <button
                type="submit"
                name="saveAction"
                value="exit"
                className="btn btn-success px-3 px-md-4 py-2 rounded-pill shadow-sm"
                style={{
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  backgroundColor: '#28a745',
                  border: 'none',
                  transition: 'all 0.3s ease',
                }}
                disabled={isSubmitting}
                onMouseOver={(e) => {
                  if (!isSubmitting) e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {isSubmitting ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                ) : (
                  <i className="fa fa-check me-2" aria-hidden />
                )}
                Update and exit
              </button>
            </div>
          </div>

          <PondDetailWorkflowTabs id={id} active="general" />

          <div className="row g-3">
            {/* Basic Information Panel */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-3 py-2" style={{ borderBottom: '2px solid #e9ecef' }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1rem', fontWeight: '600' }}>
                    <i className="fa fa-info-circle me-2" style={{ color: "var(--bs-primary)", fontSize: '0.9rem' }}></i>
                    Basic Information
                  </h5>
                </div>
                <div className="card-body px-3 py-2">
                  <div className="row g-2">
                    <div className="col-12">
                      <div className="form-floating mb-2">
                        <select
                          className={`form-control ${formErrors.PondID ? "is-invalid" : ""}`}
                          id="PondID"
                          name="PondID"
                          value={formData.PondID}
                          onChange={handlePondChange}
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        >
                          <option value="0">Select Pond</option>
                          {ponds.map((pond) => (
                            <option key={pond.id} value={pond.id}>
                              {pond.Name}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="PondID" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Pond <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.PondID && <div className="invalid-feedback">{formErrors.PondID}</div>}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-floating mb-2">
                        <input
                          type="datetime-local"
                          className={`form-control ${formErrors.Date ? "is-invalid" : ""}`}
                          id="Date"
                          value={formData.Date ? formatLocalDateTime(formData.Date) : ""}
                          onChange={(e) => handleDateChange(e, 1)}
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        />
                        <label htmlFor="Date" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Sampling/Fishing Date & Time <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.Date && <div className="invalid-feedback">{formErrors.Date}</div>}
                      </div>
                    </div>

                    <div className="col-12">
                      <FishTypesSelection
                        fishTypes={pondFishTypes}
                        selectedFishTypes={samplingFishTypes}
                        onChange={handleFishTypesChange}
                        error={formErrors.FishTypes}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sampling Details Panel */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-3 py-2" style={{ borderBottom: '2px solid #e9ecef' }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1rem', fontWeight: '600' }}>
                    <i className="fa fa-clock me-2" style={{ color: '#49b6d6', fontSize: '0.9rem' }}></i>
                    Sampling Details
                  </h5>
                </div>
                <div className="card-body px-3 py-2">
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <div className="form-floating mb-2">
                        <ClockTimeInput
                          className={`form-control ${formErrors.TimeFishingStarted ? "is-invalid" : ""}`}
                          id="TimeFishingStarted"
                          value={formData.TimeFishingStarted}
                          onChange={handleChange}
                          placeholder="1200 AM"
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        />
                        <label htmlFor="TimeFishingStarted" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Time Started (h:mm AM/PM) <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.TimeFishingStarted && (
                          <div className="invalid-feedback">{formErrors.TimeFishingStarted}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating mb-2">
                        <ClockTimeInput
                          className={`form-control ${formErrors.TimeFishingStopped ? "is-invalid" : ""}`}
                          id="TimeFishingStopped"
                          value={formData.TimeFishingStopped}
                          onChange={handleChange}
                          placeholder="1214 PM"
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        />
                        <label htmlFor="TimeFishingStopped" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Time Stopped (h:mm AM/PM) <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.TimeFishingStopped && (
                          <div className="invalid-feedback">{formErrors.TimeFishingStopped}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-floating mb-2">
                        <select
                          className={`form-control ${formErrors.SamplingMethod ? "is-invalid" : ""}`}
                          id="SamplingMethod"
                          value={formData.SamplingMethod}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '8px',
                            height: '45px',
                            fontSize: '0.9rem'
                          }}
                        >
                          <option value="">Select Sampling Method</option>
                          {SAMPLING_METHOD_OPTIONS.map((m) => (
                            <option key={m} value={m}>{displayMethodLabel(m)}</option>
                          ))}
                        </select>
                        <label htmlFor="SamplingMethod" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                          Sampling Method <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        {formErrors.SamplingMethod && (
                          <div className="invalid-feedback">{formErrors.SamplingMethod}</div>
                        )}
                      </div>
                    </div>

                    <SamplingEffortFields
                      samplingMethod={formData.SamplingMethod}
                      effortCount={effortCount}
                      onEffortCountChange={setEffortCount}
                      effortUnits={effortUnits}
                      onEffortUnitsChange={setEffortUnits}
                      anglerCount={anglerCount}
                      fisherStaffOptions={fisherStaffOptions}
                      selectedSessionAnglers={selectedSessionAnglers}
                      onSessionAnglersChange={handleSessionAnglersChange}
                      sessionAnglersError={formErrors.SessionAnglers}
                      fishSamples={fishSamples}
                      pondAnglers={pondAnglers}
                    />
                  </div>
                </div>
              </div>
            </div>

            {showWeatherPanel && (
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm h-100 donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-4 py-3" style={{ borderBottom: '2px solid #f0f0f0', background: 'linear-gradient(135deg, #fff5f0 0%, #ffffff 100%)' }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1.1rem', fontWeight: '600' }}>
                    <i className="fa fa-cloud-sun me-2" style={{ color: '#ff9800', fontSize: '1rem' }}></i>
                    Weather Conditions
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.1"
                          className={`form-control ${formErrors.AirTemperature ? "is-invalid" : ""}`}
                          id="AirTemperature"
                          value={formData.AirTemperature}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <label htmlFor="AirTemperature" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Air Temperature (°F)
                        </label>
                        {formErrors.AirTemperature && (
                          <div className="invalid-feedback">{formErrors.AirTemperature}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <select
                          className={`form-control ${formErrors.SkyClimate ? "is-invalid" : ""}`}
                          id="SkyClimate"
                          value={formData.SkyClimate}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          <option value="">Select Sky</option>
                          <option value="Sunny">Sunny</option>
                          <option value="Cloudy">Cloudy</option>
                          <option value="Partly Cloudy">Partly Cloudy</option>
                          <option value="Rain">Rain</option>
                          <option value="Snow">Snow</option>
                        </select>
                        <label htmlFor="SkyClimate" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Sky
                        </label>
                        {formErrors.SkyClimate && (
                          <div className="invalid-feedback">{formErrors.SkyClimate}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.1"
                          className={`form-control ${formErrors.WindSpeed ? "is-invalid" : ""}`}
                          id="WindSpeed"
                          value={formData.WindSpeed}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <label htmlFor="WindSpeed" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Wind Speed (mph)
                        </label>
                        {formErrors.WindSpeed && (
                          <div className="invalid-feedback">{formErrors.WindSpeed}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <select
                          className={`form-control ${formErrors.WindDirection ? "is-invalid" : ""}`}
                          id="WindDirection"
                          value={formData.WindDirection}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          <option value="">Select Wind Direction</option>
                          <option value="North">North</option>
                          <option value="Northeast">Northeast</option>
                          <option value="East">East</option>
                          <option value="Southeast">Southeast</option>
                          <option value="South">South</option>
                          <option value="Southwest">Southwest</option>
                          <option value="West">West</option>
                          <option value="Northwest">Northwest</option>
                        </select>
                        <label htmlFor="WindDirection" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Wind Direction
                        </label>
                        {formErrors.WindDirection && (
                          <div className="invalid-feedback">{formErrors.WindDirection}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <select
                          className={`form-control ${formErrors.MoonPhase ? "is-invalid" : ""}`}
                          id="MoonPhase"
                          value={formData.MoonPhase}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          <option value="">Select Moon Phase</option>
                          <option value="New">New</option>
                          <option value="First Qtr">First Qtr</option>
                          <option value="Full">Full</option>
                          <option value="Last Qtr">Last Qtr</option>
                        </select>
                        <label htmlFor="MoonPhase" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Moon Phase
                        </label>
                        {formErrors.MoonPhase && (
                          <div className="invalid-feedback">{formErrors.MoonPhase}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.1"
                          className={`form-control ${formErrors.BaroMetricPressure ? "is-invalid" : ""}`}
                          id="BaroMetricPressure"
                          value={formData.BaroMetricPressure}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#ff9800';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(255, 152, 0, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <label htmlFor="BaroMetricPressure" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Barometric Pressure (mmHg)
                        </label>
                        {formErrors.BaroMetricPressure && (
                          <div className="invalid-feedback">{formErrors.BaroMetricPressure}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Fertilization (optional) & weight/length units — card title/layout adjust when fertilization fields are hidden */}
            <div className={`col-12 ${fertilizationUnitsColClass}`}>
              <div className="card border-0 shadow-sm h-100 donezo-workflow-card">
                <div
                  className="card-header border-0 bg-transparent px-4 py-3"
                  style={{
                    borderBottom: '2px solid #f0f0f0',
                    background: showFertilizationDateType
                      ? 'linear-gradient(135deg, #f3e5f5 0%, #ffffff 100%)'
                      : 'linear-gradient(135deg, #f0f9f4 0%, #ffffff 100%)',
                  }}
                >
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1.1rem', fontWeight: '600' }}>
                    {showFertilizationDateType ? (
                      <>
                        <i className="fa fa-seedling me-2" style={{ color: '#9c27b0', fontSize: '1rem' }}></i>
                        Fertilization &amp; units
                      </>
                    ) : (
                      <>
                        <i className="fa fa-balance-scale me-2" style={{ color: '#32a932', fontSize: '1rem' }}></i>
                        Weight &amp; length units
                      </>
                    )}
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    {showFertilizationDateType && (
                    <>
                    <div className="col-12">
                      <div className="form-floating">
                        <input
                          type="datetime-local"
                          className="form-control"
                          id="FertilizationDate"
                          value={formData.FertilizationDate ? formatLocalDateTime(formData.FertilizationDate) : ""}
                          onChange={(e) => handleDateChange(e, 2)}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#9c27b0';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(156, 39, 176, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <label htmlFor="FertilizationDate" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Fertilization Date
                        </label>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="FertilizationType"
                          value={formData.FertilizationType}
                          onChange={handleChange}
                          style={{ 
                            border: '1px solid #e0e0e0',
                            borderRadius: '10px',
                            height: '50px',
                            fontSize: '0.95rem',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fafafa'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#9c27b0';
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.boxShadow = '0 0 0 3px rgba(156, 39, 176, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e0e0e0';
                            e.target.style.backgroundColor = '#fafafa';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                        <label htmlFor="FertilizationType" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Fertilization Type
                        </label>
                      </div>
                    </div>
                    </>
                    )}

                    <div className="col-12 col-md-6">
                      <div className="mb-2">
                        <WeightUnitDropDownSelection
                          selectedValue={selectedWeightUnit}
                          onChange={handleWeightUnitChange}
                          error={formErrors.WeightUnit}
                        />
                        {formErrors.WeightUnit && (
                          <div className="invalid-feedback d-block">{formErrors.WeightUnit}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="mb-2">
                        <LengthUnitDropDownSelection
                          selectedValue={selectedLengthUnit}
                          onChange={handleLengthUnitChange}
                          error={formErrors.LengthUnit}
                        />
                        {formErrors.LengthUnit && (
                          <div className="invalid-feedback d-block">{formErrors.LengthUnit}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {showWaterQualityPanel && (
            <div className="col-12">
              <div className="card border-0 shadow-sm h-100 donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-4 py-3" style={{ borderBottom: '2px solid #f0f0f0', background: 'linear-gradient(135deg, #f0f9f4 0%, #ffffff 100%)' }}>
                  <h5 className="mb-0 d-flex align-items-center" style={{ color: '#2d353c', fontSize: '1.1rem', fontWeight: '600' }}>
                    <i className="fa fa-tint me-2" style={{ color: '#32a932', fontSize: '1rem' }}></i>
                    Water Quality
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.01"
                          className={`form-control ${formErrors.WaterTemperature ? "is-invalid" : ""}`}
                          id="WaterTemperature"
                          value={formData.WaterTemperature}
                          onChange={handleChange}
                          style={WQ_FIELD_STYLE}
                          onFocus={wqFocusGreen}
                          onBlur={wqBlurDefault}
                        />
                        <label htmlFor="WaterTemperature" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Water Temperature, F
                        </label>
                        {formErrors.WaterTemperature && (
                          <div className="invalid-feedback">{formErrors.WaterTemperature}</div>
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-floating">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="form-control"
                          id="WaterClarity"
                          value={formData.WaterClarity}
                          onChange={handleChange}
                          style={WQ_FIELD_STYLE}
                          onFocus={wqFocusGreen}
                          onBlur={wqBlurDefault}
                        />
                        <label htmlFor="WaterClarity" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Water Clarity, ft.
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.01"
                          className={`form-control ${formErrors.PH ? "is-invalid" : ""}`}
                          id="PH"
                          value={formData.PH}
                          onChange={handleChange}
                          style={WQ_FIELD_STYLE}
                          onFocus={wqFocusGreen}
                          onBlur={wqBlurDefault}
                        />
                        <label htmlFor="PH" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          pH (1-14)
                        </label>
                        {formErrors.PH && <div className="invalid-feedback">{formErrors.PH}</div>}
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="form-floating">
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          id="DO"
                          value={formData.DO}
                          onChange={handleChange}
                          style={WQ_FIELD_STYLE}
                          onFocus={wqFocusGreen}
                          onBlur={wqBlurDefault}
                        />
                        <label htmlFor="DO" style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                          Dissolved Oxygen, mg/L
                        </label>
                      </div>
                    </div>
                    {WQ_EXTRA_FIELD_DEFS.slice(0, 2).map((def) => (
                      <div key={def.id} className="col-12 col-md-4">
                        <div className="form-floating">
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            id={def.id}
                            value={formData[def.id]}
                            onChange={handleChange}
                            style={WQ_FIELD_STYLE}
                            onFocus={wqFocusGreen}
                            onBlur={wqBlurDefault}
                          />
                          <label htmlFor={def.id} style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                            {def.label}
                          </label>
                        </div>
                      </div>
                    ))}
                    {WQ_EXTRA_FIELD_DEFS.slice(2, 5).map((def) => (
                      <div key={def.id} className="col-12 col-md-4">
                        <div className="form-floating">
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            id={def.id}
                            value={formData[def.id]}
                            onChange={handleChange}
                            style={WQ_FIELD_STYLE}
                            onFocus={wqFocusGreen}
                            onBlur={wqBlurDefault}
                          />
                          <label htmlFor={def.id} style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                            {def.label}
                          </label>
                        </div>
                      </div>
                    ))}
                    {WQ_EXTRA_FIELD_DEFS.slice(5, 8).map((def) => (
                      <div key={def.id} className="col-12 col-md-4">
                        <div className="form-floating">
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            id={def.id}
                            value={formData[def.id]}
                            onChange={handleChange}
                            style={WQ_FIELD_STYLE}
                            onFocus={wqFocusGreen}
                            onBlur={wqBlurDefault}
                          />
                          <label htmlFor={def.id} style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '500' }}>
                            {def.label}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default UpdatePD;